import { useState, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { useAuth, useCan } from "../../auth/AuthProvider";
import { CronField } from "../../components/CronField";
import { TimezonePicker } from "../../components/TimezonePicker";
import { Icon } from "../../components/icons";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  DeploymentBadge,
  Field,
  Input,
  LoadingBlock,
  Mono,
  PageHeader,
  Select,
  Switch,
  Textarea,
} from "../../components/ui";
import { absoluteTime, relativeTime } from "../../lib/format";
import { formatMoney } from "../../lib/money";
import { nextRun, validateCron } from "../../lib/cron";

import {
  useDeleteSchedule,
  useRunSchedule,
  useSchedule,
  useScheduleRuns,
  useUpdateSchedule,
} from "../../lib/queries";
import { ScheduleRuns } from "./ScheduleRuns";

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] tracking-[0.08em] text-fg-subtle uppercase">{label}</dt>
      <dd className="mt-1 text-sm text-fg">{children}</dd>
    </div>
  );
}

export function ScheduleDetailPage() {
  const { t } = useTranslation("schedules");
  const { scheduleId } = useParams();
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const canManage = useCan("developer");
  const navigate = useNavigate();

  const { data: schedule, isPending, error } = useSchedule(orgId, scheduleId);
  const update = useUpdateSchedule(orgId ?? "", scheduleId ?? "");
  const remove = useDeleteSchedule(orgId ?? "");
  const run = useRunSchedule(orgId ?? "", scheduleId ?? "");
  /*
   * Read here as well as in the panel below, and it is the same cache entry:
   * the header's "Last run" is the newest of these, and the engine's record
   * covers scheduled and manual runs alike. Before this existed the header
   * could only report the manual ones, which are the runs nobody wonders about.
   */
  const runs = useScheduleRuns(orgId, scheduleId);
  const lastRun = runs.data?.data[0] ?? null;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{
    name: string;
    description: string;
    cronExpression: string;
    command: string;
    shellType: "bash" | "sh";
    timezone: string;
  } | null>(null);
  const [runNotice, setRunNotice] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;
  if (!schedule) return <Alert>{t("notFound")}</Alert>;

  function beginEdit() {
    if (!schedule) return;
    setDraft({
      name: schedule.name,
      description: schedule.description ?? "",
      cronExpression: schedule.cronExpression,
      command: schedule.command,
      shellType: schedule.shellType,
      timezone: schedule.timezone,
    });
    setEditing(true);
  }

  async function save() {
    if (!draft) return;
    await update.mutateAsync({
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      cronExpression: draft.cronExpression.trim(),
      command: draft.command.trim(),
      shellType: draft.shellType,
      timezone: draft.timezone,
    });
    setEditing(false);
    setDraft(null);
  }

  async function destroy() {
    if (!scheduleId) return;
    const result = await remove.mutateAsync(scheduleId);
    navigate("/schedules", {
      state:
        result.engineCleanup === "orphaned"
          ? {
              notice: t("detail.orphaned"),
            }
          : undefined,
    });
  }

  const draftCheck = draft ? validateCron(draft.cronExpression) : null;
  const upcoming =
    schedule.enabled && schedule.provisioned
      ? nextRun(schedule.cronExpression, schedule.timezone)
      : null;

  return (
    <div className="space-y-6">
      {/*
        Both places this job sits, because it belongs to two: the org-wide list
        and one app. The app was in the header's description until a job with a
        description of its own pushed it out, which left the page with no link
        to the thing it runs inside.
      */}
      <nav className="flex items-center gap-1.5 text-sm text-fg-muted">
        <Icon name="chevron" className="size-3.5 rotate-180" />
        <Link to="/schedules" className="transition-colors hover:text-fg">
          {t("title")}
        </Link>
        <span className="text-fg-subtle">/</span>
        <Link
          to={`/apps/${schedule.appId}/schedules`}
          className="transition-colors hover:text-fg"
        >
          {schedule.appName}
        </Link>
      </nav>

      <PageHeader
        title={schedule.name}
        description={schedule.description ?? undefined}
        action={
          canManage && !editing ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={!schedule.provisioned}
                loading={run.isPending}
                onClick={async () => {
                  await run.mutateAsync();
                  // "Started", never "succeeded" — the engine reports no
                  // outcome, so claiming one would be an invention.
                  setRunNotice(t("detail.runStarted"));
                }}
              >
                {t("detail.runNow")}
              </Button>
              <Button variant="secondary" onClick={beginEdit}>
                {t("detail.edit")}
              </Button>
            </div>
          ) : undefined
        }
      />

      {update.error && <Alert>{update.error.message}</Alert>}
      {remove.error && <Alert>{remove.error.message}</Alert>}
      {run.error && <Alert>{run.error.message}</Alert>}
      {runNotice && <Alert tone="success">{runNotice}</Alert>}

      {!schedule.provisioned && (
        <Alert>{t("detail.unprovisioned")}</Alert>
      )}

      {schedule.appState === "stopped" && (
        <Alert tone="warning">
          <span>
            <Trans
              i18nKey="detail.appStopped"
              ns="schedules"
              values={{ app: schedule.appName }}
              components={{
                app: <Link to={`/apps/${schedule.appId}`} className="underline" />,
              }}
            />
          </span>
        </Alert>
      )}

      {editing && draft ? (
        <Card>
          <CardHeader title={t("detail.editTitle")} description={t("detail.editLead")} />
          <div className="space-y-5 p-5">
            <Field label={t("detail.name")} hint={t("detail.nameHint")}>
              <Input
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                maxLength={60}
              />
            </Field>

            <Field label={t("detail.description")} hint={t("detail.descriptionHint")}>
              <Input
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                maxLength={500}
              />
            </Field>

            <CronField
              value={draft.cronExpression}
              onChange={(cronExpression) => setDraft({ ...draft, cronExpression })}
              timezone={draft.timezone}
            />

            <TimezonePicker
              value={draft.timezone}
              onChange={(timezone) => setDraft({ ...draft, timezone })}
            />

            <Field label={t("detail.command")} hint={t("detail.commandHint")}>
              <Textarea
                value={draft.command}
                onChange={(event) => setDraft({ ...draft, command: event.target.value })}
                rows={3}
                maxLength={2000}
                spellCheck={false}
                className="font-mono"
              />
            </Field>

            <Field label={t("detail.shell")} hint={t("detail.shellHint")}>
              <Select
                value={draft.shellType}
                onChange={(event) =>
                  setDraft({ ...draft, shellType: event.target.value as "bash" | "sh" })
                }
              >
                <option value="bash">bash</option>
                <option value="sh">sh</option>
              </Select>
            </Field>

            <div className="flex justify-end gap-2 border-t border-line pt-5">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(false);
                  setDraft(null);
                }}
              >
                {t("detail.cancel")}
              </Button>
              <Button
                disabled={!draftCheck?.ok}
                loading={update.isPending}
                onClick={() => void save()}
              >
                {t("detail.save")}
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader
              title={t("detail.schedule")}
              action={
                canManage ? (
                  <div className="flex items-center gap-2.5">
                    <span className="text-[13px] text-fg-subtle">
                      {schedule.enabled ? t("detail.enabled") : t("detail.paused")}
                    </span>
                    <Switch
                      checked={schedule.enabled}
                      disabled={update.isPending}
                      onChange={(enabled) => void update.mutateAsync({ enabled })}
                      label={t("detail.enabled")}
                    />
                  </div>
                ) : undefined
              }
            />
            <dl className="grid grid-cols-2 gap-x-6 gap-y-5 p-5 sm:grid-cols-4">
              <Stat label={t("detail.runs")}>
                {schedule.cronDescription}
                <div className="mt-0.5 text-[11px] text-fg-subtle">{schedule.timezone}</div>
              </Stat>
              <Stat label={t("detail.nextRun")}>
                {upcoming ? (
                  <span title={absoluteTime(upcoming.toISOString())}>
                    {relativeTime(upcoming.toISOString())}
                  </span>
                ) : (
                  /* Nothing rather than a countdown: a paused or unprovisioned
                     job has no next run, and a time beside it would be a promise
                     the platform is not keeping. */
                  <span className="text-fg-subtle">
                    {schedule.enabled ? t("detail.notCreated") : t("detail.paused")}
                  </span>
                )}
              </Stat>
              {/* The engine's own record, which covers scheduled runs as well
                  as manual ones. When it cannot be read we fall back to the
                  manual timestamp and say so in the label, rather than
                  reporting "never" for a job that has been running nightly. */}
              {lastRun ? (
                <Stat label={t("detail.lastRun")}>
                  <span className="flex items-center gap-2">
                    <DeploymentBadge status={lastRun.status} />
                    <span title={absoluteTime(lastRun.createdAt)}>
                      {relativeTime(lastRun.createdAt)}
                    </span>
                  </span>
                </Stat>
              ) : (
                <Stat
                  label={runs.data?.unavailable ? t("detail.lastManualRun") : t("detail.lastRun")}
                >
                  {schedule.lastManualRunAt ? (
                    <span title={absoluteTime(schedule.lastManualRunAt)}>
                      {relativeTime(schedule.lastManualRunAt)}
                    </span>
                  ) : (
                    <span className="text-fg-subtle">{t("detail.never")}</span>
                  )}
                </Stat>
              )}
              <Stat label={t("detail.expression")}>
                <Mono className="text-[12px]">{schedule.cronExpression}</Mono>
              </Stat>
            </dl>
          </Card>

          <Card>
            <CardHeader
              title={t("detail.commandTitle")}
              description={t("detail.commandLead", { shell: schedule.shellType })}
            />
            <div className="p-5">
              <Mono className="block break-all text-[13px] leading-6 text-fg">
                {schedule.command}
              </Mono>
            </div>
          </Card>

          <ScheduleRuns
            orgId={orgId}
            scheduleId={scheduleId}
            provisioned={schedule.provisioned}
          />

          <Card>
            <CardHeader
              title={t("detail.cost")}
              description={t("detail.costLead")}
            />
            <dl className="grid grid-cols-2 gap-x-6 gap-y-5 p-5 sm:grid-cols-3">
              <Stat label={t("detail.price")}>
                <Trans
                  i18nKey="detail.perMonth"
                  ns="schedules"
                  values={{ amount: `$${(schedule.price.monthlyCents / 100).toFixed(2)}` }}
                  components={{ amount: <span className="tabular-nums" /> }}
                />
              </Stat>
              <Stat label={t("detail.thisMonth")}>
                <span className="tabular-nums">
                  {formatMoney(schedule.costToDate.monthMicros)}
                </span>
              </Stat>
              <Stat label={t("detail.sinceCreated")}>
                <span className="tabular-nums">
                  {formatMoney(schedule.costToDate.sinceCreatedMicros)}
                </span>
              </Stat>
            </dl>
          </Card>
        </>
      )}

      {canManage && !editing && (
        <Card>
          <CardHeader
            title={t("detail.deleteTitle")}
            description={t("detail.deleteLead")}
          />
          <div className="flex flex-wrap items-center justify-end gap-4 p-5">
            {confirmingDelete ? (
              <>
                <p className="text-[13px] text-fg">
                  <Trans
                    i18nKey="detail.deleteConfirm"
                    ns="schedules"
                    values={{ name: schedule.name }}
                    components={{ name: <span className="font-medium" /> }}
                  />
                </p>
                <div className="flex shrink-0 gap-2">
                  <Button variant="secondary" onClick={() => setConfirmingDelete(false)}>
                    {t("detail.keep")}
                  </Button>
                  <Button variant="danger" loading={remove.isPending} onClick={() => void destroy()}>
                    {t("detail.delete")}
                  </Button>
                </div>
              </>
            ) : (
              <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
                {t("detail.deleteAction")}
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
